import React, { useEffect, useState } from 'react';
import {
  Page,
  FlexWrapper,
  PopConfirm,
} from '@/components';
import {
  StyledCardHeader,
  StyledProjectCard,
  StyledListItem,
} from '@/components/project-card/project-card.styled';
import { useProjectStore } from '@/stores';
import { Box, Typography, ToggleButton, ToggleButtonGroup, styled, CardContent, IconButton, ListItemText, ListItemIcon, List, Menu } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { ThreeDots, Hub, Sensor, CableUSB, WirelessConnection, CableConnection, Battery, SensorIn, UltrasonicWave, FlatWave } from '@/components/icons';
import { useDateTimeFormat, useUnitType } from '@/hooks';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { StyledMenuItem } from '@/components/pour-card';
import { toast } from 'react-toastify';
import { routesDetails } from '@/constants';
import { t } from 'i18next';
import { ITechnicalScreenDevice } from '@/models/projects';
import { temperatureToViewTemperature } from '@/utils';
import theme from '@/theme/index';
import { INavButton } from '@/components/header';
import { useLocation } from "react-router-dom";


const StyledToggleButton = styled(ToggleButtonGroup)(({ theme }) => ({
  borderRadius: theme.typography.pxToRem(24),
  width: '100%',
  flexWrap: 'wrap',
  '& .MuiButtonBase-root.MuiButtonBase-root': {
    backgroundColor: theme.palette.grey.A100,
    border: 0,
    minWidth: '63px',
    margin: '5px 1.5px',
    borderRadius: theme.typography.pxToRem(24),
    color: theme.palette.black.light,
    textTransform: 'capitalize',
    padding: `${theme.typography.pxToRem(8)} ${theme.typography.pxToRem(12)}`,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.darker,
      color: theme.palette.common.white,
    },
    '&:last-child': {
      marginRight: '0',
      marginLeft: '0',
    },
  },
}));

const isLegalTemperature=(temp:number):boolean=> (-20 < temp && temp < 120)

const is_device_on_alert_mode=(sensor:ITechnicalScreenDevice):boolean=>
   (!isDeviceConnected(sensor) || ((sensor.battery??0)<5)) && sensor.isCommunicate;


const isDeviceConnected=(sensor:ITechnicalScreenDevice):boolean=>
  isLegalTemperature(sensor.temperature) && (0 < sensor.resistivity && sensor.resistivity < 1000000) && !sensor.isFlatWave

const SensorDetails = ({ sensors }:{ sensors: ITechnicalScreenDevice[] }) => {
  const { isMetric } = useUnitType();

  const { formatDate } = useDateTimeFormat();
  // Function to determine battery color based on percentage
  const getBatteryColorPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) {
      return  theme.palette.text.disabled; // Default color if no battery value
    }
    if (percentage < 40) {
      return '#bc544b'; // Red color for battery < 40%
    } else if (percentage < 80) {
      return '#cda984'; // Yellow color for 40% <= battery < 80%
    } else {
      return '#009999'; // Green color for battery >= 80%
    }
  };

  const getTemperatureColor = (temperature:number) => {
    const goodCelsiusMinThreshold = 0
    const goodCelsiusMaxThreshold = 90
    const midCelsiusRangeThreshold = 30
    if (temperature > goodCelsiusMinThreshold
       && temperature < goodCelsiusMaxThreshold)
       return '#009999'; // Green
      
    if ((temperature > goodCelsiusMaxThreshold 
       && temperature < goodCelsiusMaxThreshold + midCelsiusRangeThreshold) ||
       (temperature < goodCelsiusMinThreshold 
       && temperature > goodCelsiusMinThreshold - midCelsiusRangeThreshold)
       )
       return '#cda984'; // Yellow

    return '#bc544b'; // Red
  };

  const sortedDevice = sensors.sort((a, b) => {
    if (a.isActive !== b.isActive) {
      return Number(b.isActive) - Number(a.isActive); // Sort by active status (true/false)
    }else if(is_device_on_alert_mode(a) !== is_device_on_alert_mode(b)) { 
      return Number(is_device_on_alert_mode(b)) - Number(is_device_on_alert_mode(a))
    }
    return a.number - b.number; // Sort by device number numerically
  });

  return (
    <Box>
      {sortedDevice.map((device, i) => {
        const sensorName = device.number ? device.number : device.name;
        const inactiveColor =  theme.palette.text.disabled;
        const alertcolor = '#bc544b';
        const activeColor = device.isActive ? '#009999' : inactiveColor; // Green if active, grey if not
        const temperatureColor = device.isActive && isLegalTemperature(device.temperature) ? getTemperatureColor(device.temperature) : inactiveColor;
    
        const inConcreteColor = device.isActive && device.isInConcrete ? activeColor : inactiveColor;
        const communicationColor = device.isActive && device.isCommunicate ? activeColor : (device.isActive ? alertcolor : inactiveColor);
        const batteryValue = device.battery ?? 'undefined';
        const batteryColor = device.isActive
          ? device.number !== null 
            ? (batteryValue === null || batteryValue === 'undefined' ? inactiveColor : getBatteryColorPercentage(batteryValue))
            : (batteryValue === null || batteryValue === 'undefined' ? inactiveColor : '#696868')
          : inactiveColor;
        const batterySymbol = device.number !== null ? '%' : 'mV';
        const displayLastBroadcast = device.isActive ? formatDate(device.lastBroadcast, 'time') : '';
        const lastBroadcast = displayLastBroadcast !== 'N/A' ? displayLastBroadcast : '';
        const headerBackgroundColor = device.isActive ? 
                                        is_device_on_alert_mode(device)? alertcolor : theme.palette.primary.darker 
                                        : inactiveColor;
        const isFlatWave = device.isFlatWave;
        const viewTemperature:string = isLegalTemperature(device.temperature) ?  temperatureToViewTemperature(device.temperature,isMetric): t('sensorDisconnected')
        
        ///
        return (
          <StyledProjectCard key={i} data-testid={device.number ?? device.name}>
            <StyledCardHeader
              action={
                <IconButton 
                aria-label='Edit'
                style={{ marginRight: '0px' }} // Adjust the margin to move the icon to the left
              >
                <ThreeDots />
              </IconButton>
              }
              style={{ backgroundColor: headerBackgroundColor }}
              title={
                <>
                  {sensorName}<br />
                  Location: {device.location || 'Unknown Location'}<br />
                  Pour: {device.pourName || 'Unknown Pour Name'}
                </>
              }
            />
            <CardContent>
              <List dense disablePadding>
                <StyledListItem>
                  <ListItemIcon>
                    <WirelessConnection color={communicationColor} aria-label="Communication"/>
                  </ListItemIcon>
                  <ListItemText>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>
                        {device.isCommunicate ? 'Online' : 'Offline'}
                      </span>
                      {!device.isCommunicate && (
                        <>
                          {device.hourFromBroadcast && (
                            <span style={{ fontSize: '0.75rem', marginRight: '4px' }}>
                              {device.hourFromBroadcast} hrs
                            </span>
                          )}
                          {lastBroadcast && (
                            <span style={{ fontSize: '0.75rem' }}>
                              ({lastBroadcast})
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </ListItemText>
                </StyledListItem>
                <StyledListItem>
                  <ListItemIcon>
                    <CableConnection color={temperatureColor} aria-label="Connected"/>
                  </ListItemIcon>
                  <ListItemText>
                    <span>{viewTemperature}</span>
                  </ListItemText>
                </StyledListItem>
                <StyledListItem>
                  <ListItemIcon>
                    <SensorIn color={inConcreteColor} aria-label="InConcrete"/>
                  </ListItemIcon>
                  <ListItemText>
                    <span>{device.isInConcrete ? t('inConcrete') : t('notInConcrete')}</span>
                  </ListItemText>
                </StyledListItem>
                {device.isInConcrete
                ? <StyledListItem>
                  <ListItemIcon>
                    {isFlatWave ? <FlatWave color={'#bc544b'} aria-label="FlatWave"/> :<UltrasonicWave color={activeColor} aria-label="EnergyWave"/>}
                  </ListItemIcon>
                  <ListItemText>
                    <span>{isFlatWave ? t('flatWave') : t('energyWave')}</span>
                  </ListItemText>
                </StyledListItem> : <></>}
                
                <StyledListItem>
                  <ListItemIcon>
                    <Battery color={batteryColor}/>
                  </ListItemIcon>
                  <ListItemText>
                    <span>{batteryValue != null && batteryValue !== 0 && batteryValue}{batterySymbol}</span>
                  </ListItemText>
                </StyledListItem>
              </List>
            </CardContent>
          </StyledProjectCard>
        );
      })}
    </Box>
  );
};

const HubDetails = ({ hubs, anchorEl, selectedHubId, handleClick, handleEditClick, handleDeleteClick, handleClose }) => {
  const { formatDate } = useDateTimeFormat();

  return (
    <Box>
      {hubs.map((hub, i) => {
        const defaultColor =  theme.palette.text.disabled;
        const connectedColor = hub.isUsbConnected ? '#009999' : defaultColor;
        const communicationColor = hub.isCommunicate ? '#009999' : '#bc544b';
        const displayLastBroadcast = formatDate(hub.lastBroadcast, 'time');
        const lastBroadcast = displayLastBroadcast !== 'N/A' ? displayLastBroadcast : '';
        const batteryVoltage = hub.hubBattery ?? t('NA');
        const batteryColor = batteryVoltage === null || batteryVoltage === 'undefined' ? defaultColor : '#696868';

        return (
          <StyledProjectCard key={i} data-testid={hub.hubId}>
            <StyledCardHeader
              action={
                <>
                  <IconButton 
                    aria-label='Options'
                    onClick={(event) => handleClick(event, hub.hubId)}
                    style={{ marginRight: '0px' }}
                  >
                    <ThreeDots />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && selectedHubId === hub.hubId}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <StyledMenuItem onClick={handleEditClick}>
                      <span><EditIcon /></span> Edit Hub
                    </StyledMenuItem>
                    <StyledMenuItem onClick={e => {
                        handleDeleteClick(selectedHubId);
                      }}>
                      <span><CloseIcon /></span> Delete Hub
                    </StyledMenuItem>
                  </Menu>
                </>
              }
              title={
                <>
                  {hub.number}&nbsp;{hub.name || 'Hub'}<br />
                  {hub.location || 'Unknown Location'}<br />
                  TI ID: {hub.hubId || 'Unknown ID'}
                </>
              }
            />
            <CardContent>
              <List dense disablePadding>
                <StyledListItem>
                  <ListItemIcon>
                    <WirelessConnection color={communicationColor} aria-label="Communication"/>
                  </ListItemIcon>
                  <ListItemText>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>
                        {hub.isCommunicate ? 'Online' : 'Offline'}
                      </span>
                      {!hub.isCommunicate && (
                        <>
                          {hub.hourFromBroadcast && (
                            <span style={{ fontSize: '0.75rem', marginRight: '4px' }}>
                              {hub.hourFromBroadcast} hrs
                            </span>
                          )}
                          {lastBroadcast && (
                            <span style={{ fontSize: '0.75rem' }}>
                              ({lastBroadcast})
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </ListItemText>
                </StyledListItem>
                <StyledListItem>
                  <ListItemIcon>
                    <CableUSB color={connectedColor} aria-label="USB"/>
                  </ListItemIcon>
                  <ListItemText>
                    <span>
                      {hub.isUsbConnected ? t('USBConnected') : t('USBDisconnected')}
                    </span>
                  </ListItemText>
                </StyledListItem>
                <StyledListItem>
                  <ListItemIcon>
                    <Battery color={batteryColor}/>
                  </ListItemIcon>
                  <ListItemText>
                    <span>{batteryVoltage != null && batteryVoltage !== 0 && batteryVoltage} mV</span>
                  </ListItemText>
                </StyledListItem>
              </List>
            </CardContent>
          </StyledProjectCard>
          
        );
      })}
    </Box>
  );
};

const ProjectStatus = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const getTechnicalScreen = useProjectStore((state) => state.getTechnicalScreen);
  const technicalScreenData = useProjectStore((state) => state.technicalScreenData);
  const { deleteHub } = useProjectStore();
  const [view, setView] = useState<'hubs' | 'sensors'>('sensors');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedHubId, setSelectedHubId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (projectId) {
      getTechnicalScreen(Number(projectId));
    }
  }, [projectId, getTechnicalScreen, location.key]);

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: 'hubs' | 'sensors') => {
    setView(newView);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, hubId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedHubId(hubId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedHubId(null);
  };

  const handleEditClick = () => {
    navigate(
      routesDetails.editHub(
        selectedHubId,
        projectId
      )
    );
  };

  const handleDeleteClick = (hubId: string) => {
    setIsModalOpen(true);
    setSelectedHubId(hubId);
  };

  const handleConfirmDelete = async (selectedHubId: string) => {
    await deleteHub(selectedHubId, async () => {
      await getTechnicalScreen(Number(projectId));
      setIsModalOpen(false);
      setSelectedHubId(undefined);
      toast.success(t('hubDeletedSuccessfully'), {
        autoClose: 1000,
      });
    });
  };

  return (
    <Page
      handleArrowClick={() => navigate(routesDetails.project(projectId.toString()))}
      navTitle={'Technical Status'}
      MenuTogglerType={INavButton.goBack}
    >
      <Box sx={{ margin: '1px' }}>
        <FlexWrapper
          alignItems='center'
          justifyContent='center'
          sx={{ marginTop: '20px', marginBottom: '8px' }}
        >
          <StyledToggleButton
            value={view}
            exclusive
            onChange={handleViewChange}
            aria-label='view toggle'
          >
            <ToggleButton value='sensors' aria-label='Sensors'>
              <Sensor />
              <span style={{ marginLeft: '8px' }}>Devices</span>
            </ToggleButton>
            <ToggleButton value='hubs' aria-label='Hubs'>
              <Hub />
              <span style={{ marginLeft: '8px' }}>Hubs</span>
            </ToggleButton>
          </StyledToggleButton>
        </FlexWrapper>
        {technicalScreenData.length > 0 ? (
          view === 'sensors' ? (
            technicalScreenData.map((data, index) => (
              <SensorDetails key={`sensor-${index}`} sensors={data.project_devices} />
            ))
          ) : (
            technicalScreenData.map((data, index) => (
              <HubDetails 
                key={`hub-${index}`} 
                hubs={data.project_hubs}
                anchorEl={anchorEl}
                selectedHubId={selectedHubId} 
                handleClick={handleClick} 
                handleEditClick={handleEditClick} 
                handleDeleteClick={() => {
                  handleDeleteClick(selectedHubId); // Pass selectedHubId directly
                }}
                handleClose={handleClose}/>
            ))
          )
        ) : (
          <Typography>No technical screen data available.</Typography>
        )}
      </Box>
      <PopConfirm
        isOpen={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={() => {
          handleConfirmDelete(selectedHubId); // Pass selectedHubId directly
          setIsModalOpen(false); // Close the modal
        }}
        title={'Delete Hub'}
        subTitle={'Are you sure you want to delete this hub? This action cannot be undone.'}
      />
    </Page>
  );
};

export default ProjectStatus;
